import throttle from 'lodash.throttle'
import {CorePlugin, Playback, type SupportedVersion} from '@clappr/core'
import {Events} from 'common/types/events'
import {activateYandexMetrika, reachGoal} from 'utils/metrics'
import type {Options} from 'common/types/options'
import {
  type YandexMetrikaConfig,
  AdvertisementBlockType
} from 'common/api/records'
import type {Docking} from 'common/plugins/docking'
import type {
  Advertisement,
  AdvertisementEvent,
  AdvertisementError
} from 'common/plugins/advertisement'

const HALF_MINUTE = 30
const MINUTE = 60

export class YandexMetrika<T extends Options> extends CorePlugin<T> {
  public static readonly TIMEUPDATE_THROTTLE_TIMEOUT = 2000

  // save ready state in one frame session
  private static ready = false

  private static ADVERTISEMENT_EVENTS_PARAMS: Record<
    string,
    Record<string, keyof YandexMetrikaConfig>
  > = {
    [AdvertisementBlockType.PRE_ROLL]: {
      event: 'preRollEvent',
      provider: 'preRollAdProvider'
    }
  }

  private static AD_SYSTEM_PROVIDERS_DOMAINS: Record<string, string> = {
    adfox_gpmd: 'ads.adfox.ru/378150',
    rb_gpmd: 'ad.mail.ru',
    adfox_rb: 'ads.adfox.ru/171817'
  }

  // save loaded state in one record session
  private loaded = false
  private started = false
  private viewedTime: {[key: number]: number} = {}

  public get name(): string {
    return 'yametrika'
  }

  public get supportedVersion(): SupportedVersion {
    return {min: '0.5.0'}
  }

  public get params(): YandexMetrikaConfig[] {
    return this.core.options.yametrika || ([] as YandexMetrikaConfig[])
  }

  private get canPlay(): boolean {
    return this.core.activePlayback.getPlaybackType() !== Playback.NO_OP
  }

  private get docking(): Docking<T> | null {
    return this.core.getPlugin('docking') as Docking<T> | null
  }

  private get advertisement(): Advertisement<T> | null {
    return this.core.getPlugin('advertisement') as Advertisement<T> | null
  }

  private get siteInfoParams(): Record<string, any> {
    const {live, referrer, playList, yandexMetrikaVideoType} = this.options
    // NOTE: id для видео и старых трансляций, uuid для новых трансляций
    const id = playList?.id ?? playList?.uuid
    if (!playList || !id) return {}

    const {activePlayback} = this.core
    const videoType =
      yandexMetrikaVideoType ??
      (live || activePlayback?.getPlaybackType() === Playback.LIVE
        ? 'live'
        : 'video')

    return {
      [videoType]: {
        [id]: {
          [playList.title]: referrer
        }
      }
    }
  }

  public bindEvents(): void {
    if (!this.core.isReady) {
      this.onTimeUpdate = throttle(
        this.onTimeUpdate,
        YandexMetrika.TIMEUPDATE_THROTTLE_TIMEOUT,
        {trailing: false}
      )
    }

    this.listenToOnce(this.core, Events.CORE_READY, this.onReady)
    this.listenTo(
      this.core,
      Events.CORE_ACTIVE_CONTAINER_CHANGED,
      this.onActiveContainerChange
    )

    const {activeContainer} = this.core

    if (activeContainer) {
      this.listenTo(activeContainer, Events.CONTAINER_PLAY, this.onPlay)
      this.listenTo(
        activeContainer,
        Events.CONTAINER_TIMEUPDATE,
        this.onTimeUpdate
      )
      this.listenTo(activeContainer, Events.CONTAINER_ENDED, this.onEnded)
    }

    if (this.docking) {
      this.listenTo(this.docking, Events.DOCKING_CHANGED, this.onDockingChange)
    }

    if (this.advertisement) {
      this.listenTo(
        this.advertisement,
        Events.ADVERTISEMENT_LOADED,
        this.onAdLoaded
      )
      this.listenTo(
        this.advertisement,
        Events.ADVERTISEMENT_WARNING,
        this.onAdWarning
      )
    }
  }

  private onActiveContainerChange(): void {
    this.stopListening()
    this.bindEvents()
  }

  private onReady(): void {
    if (this.canPlay) {
      if (!YandexMetrika.ready) {
        this.params.forEach(({yandexId, defer}) => {
          activateYandexMetrika(yandexId, {defer: !!defer})
        })

        YandexMetrika.ready = true
      }

      if (!this.loaded) {
        this.params.forEach(({load, yandexId}) => {
          if (load) {
            reachGoal(yandexId, load, this.siteInfoParams)
          }
        })

        this.loaded = true
      }
    }
  }

  private onPlay(): void {
    if (this.started) {
      return
    }

    this.params.forEach(({yandexId, start}) => {
      if (start) {
        reachGoal(yandexId, start, this.siteInfoParams)
      }
    })

    this.started = true
  }

  private onTimeUpdate(progress: {current: number}): void {
    const isPlaying = this.core.activeContainer?.isPlaying()

    if (!isPlaying) {
      return
    }

    this.params.forEach(
      ({yandexId, watchTime, watchTime30s, watchTime1m, watchTime90s}) => {
        const viewedTime =
          Math.floor(progress.current / HALF_MINUTE) * HALF_MINUTE

        const watchEvents = []

        watchEvents[0] = watchTime
        watchEvents[HALF_MINUTE] = watchTime30s
        watchEvents[MINUTE] = watchTime1m
        watchEvents[MINUTE + HALF_MINUTE] = watchTime90s

        watchEvents
          .filter(
            (watchEvent, time) =>
              watchEvent === watchTime ||
              (viewedTime === time &&
                viewedTime > (this.viewedTime[yandexId] ?? -1))
          )
          .forEach((watchEvent) => {
            if (watchEvent) {
              reachGoal(yandexId, watchEvent, this.siteInfoParams)
            }

            if (watchEvent !== watchTime) {
              this.viewedTime[yandexId] = viewedTime
            }
          })
      }
    )
  }

  private onEnded(): void {
    if (!this.started) {
      return
    }

    this.params.forEach(({yandexId, played}) => {
      if (played) {
        reachGoal(yandexId, played, this.siteInfoParams)
      }
    })

    this.started = false
    this.viewedTime = {}
  }

  private onDockingChange(docked: boolean): void {
    if (!docked && !this.docking?.enabled) {
      this.params.forEach(({yandexId, floatingDisabled}) => {
        if (floatingDisabled) {
          reachGoal(yandexId, floatingDisabled, this.siteInfoParams)
        }
      })
    }
  }

  private onAdLoaded(event: AdvertisementEvent): void {
    this.params.forEach(({yandexId, adload}) => {
      if (adload) {
        reachGoal(yandexId, adload, this.siteInfoParams)
      }
    })

    this.onAdRequested(event)
  }

  private onAdWarning(error: AdvertisementError): void {
    this.onAdRequested(error.raw)
  }

  private onAdRequested(event: AdvertisementEvent): void {
    const {blockType, requestTags = []} = event

    const advertisementEventData =
      YandexMetrika.ADVERTISEMENT_EVENTS_PARAMS[blockType]

    if (!advertisementEventData) {
      return
    }

    const {event: eventKey, provider: providerKey} = advertisementEventData

    this.params.forEach(
      ({yandexId, [eventKey]: blockEvent, [providerKey]: blockAdProvider}) => {
        if (!blockEvent || !blockAdProvider) {
          return
        }

        const domain =
          YandexMetrika.AD_SYSTEM_PROVIDERS_DOMAINS[blockAdProvider as string]
        const isMatchedAnalyticsAdSystemsDomain =
          domain &&
          // eslint-disable-next-line security/detect-non-literal-regexp
          requestTags.some((url: string) => url?.match(new RegExp(domain)))

        if (isMatchedAnalyticsAdSystemsDomain) {
          reachGoal(yandexId, blockEvent as string, this.siteInfoParams)
        }
      }
    )
  }
}
